// ─── Where the user came from ────────────────────────────────────────────────
// utm_source / utm_medium / utm_campaign, read from the URL on the first page
// this browser opens, kept in localStorage, and written to the user's profile
// once — the first time a signed-in, non-guest session has a profile row with
// no source recorded. After that it is never overwritten: attribution is the
// FIRST touch, and a later visit through a different link does not rewrite how
// somebody actually arrived.
//
// Three fields, nothing else. No referrer, no landing path, no click id — the
// question is "which campaign", and anything more is a trail we have no reason
// to keep. The parameters are stripped from the address bar after they are
// read, so a copied link does not carry somebody else's campaign.

import { supabase } from './supabase';

const STORE = 'si_attribution';
const FIELDS = ['utm_source', 'utm_medium', 'utm_campaign'];
const MAX = 120;   // the column width; a longer value is somebody's mistake

function clean(v) {
  const s = String(v || '').trim();
  return s && s.length <= MAX ? s : null;
}

// Call once, as early as possible. Returns the stored attribution, or null.
export function captureAttribution() {
  let stored = read();
  try {
    const q = new URLSearchParams(window.location.search);
    const fresh = {};
    for (const f of FIELDS) {
      const v = clean(q.get(f));
      if (v) fresh[f] = v;
    }
    if (Object.keys(fresh).length) {
      // First touch wins: an existing record is not replaced.
      if (!stored) {
        stored = fresh;
        try { localStorage.setItem(STORE, JSON.stringify(fresh)); } catch { /* private mode */ }
      }
      // Out of the address bar either way.
      for (const f of FIELDS) q.delete(f);
      const rest = q.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash);
    }
  } catch { /* no URL, no storage — nothing recorded, nothing broken */ }
  return stored;
}

export function read() {
  try {
    const raw = localStorage.getItem(STORE);
    if (!raw) return null;
    const o = JSON.parse(raw);
    const out = {};
    for (const f of FIELDS) if (clean(o?.[f])) out[f] = o[f];
    return Object.keys(out).length ? out : null;
  } catch { return null; }
}

// Writes the three fields to the user's own profiles row, once. The update is
// conditional in SQL — `is null` on utm_source — so a second device cannot
// overwrite what the first one recorded, and a guest is never written at all.
//
// Fire-and-forget: the columns may not exist yet in a given environment
// (migrations/sprint7.sql), and measurement must never block a sign-in.
export async function saveAttribution(userId, { isGuest = false } = {}) {
  if (!supabase || !userId || isGuest) return;
  const a = read();
  if (!a) return;
  try {
    const { error } = await supabase
      .from('profiles')
      .update(a)
      .eq('id', userId)
      .is('utm_source', null);
    if (error) console.warn('[attribution] save skipped:', error.code, error.message);
  } catch { /* ignore */ }
}
